import React from 'react';
import { SegmentedControl, Button, Card, Alert } from '@parsina/framework';

import { C, CT } from '../../../constants/theme';
import { useTranslation } from '../../../i18n/I18nProvider.jsx';
import CompactChat from '../../../components/clinical/CompactChat.jsx';
import { OutOfCredits, CostChip, BalanceLine } from '../../../components/common/ParsehCost.jsx';
import { AiAnswerNote } from '../../../components/common/PracticeNotice.jsx';
import { COPILOT_TABS } from '../Visit.shared.js';

// دستیار — three tabs over ONE thread.
//
//   پرسش      the visit's chat: `askInEncounter`, bound to this encounter's
//             copilot thread, created on the first question.
//   افتراقی    the last differential, re-runnable.
//   پیشنهادها  the last SOAP and order suggestions, re-openable — because a
//             student closes the review sheet to look at the note, and the
//             answer they paid for must still be there when they come back.
//
// ── Out of پارسه changes THIS column and nothing else ────────────────────
//
// core.md §7.5: «the clinical editors never depend on credit». So a student with
// an empty wallet keeps a fully working note, diagnosis list, prescription and
// order list; only the assistant says «شارژ پارسه» — and it says it here, where
// the button they pressed is, rather than as a banner over the whole workspace.

export default function CopilotPanel({ vm, tab, onTab }) {
  const { t } = useTranslation();
  const { copilot } = vm;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }} data-testid="copilot-panel">
      <SegmentedControl
        options={COPILOT_TABS.map((id) => ({ value: id, label: t(`visit.copilot.${id}`) }))}
        value={tab}
        onChange={onTab}
        size="sm"
        ariaLabel={t('visit.copilot.aria')}
        data-testid="copilot-tabs"
      />

      <BalanceLine balance={vm.balance} />

      {copilot.needsTopUp && (
        <OutOfCredits onTopUp={vm.goToCredits} />
      )}
      {copilot.error && (
        <Alert variant="error" title={t('errors.generic')} data-testid="copilot-error">
          {copilot.error.message}
        </Alert>
      )}

      {tab === 'ask' && (
        <CompactChat
          messages={copilot.messages}
          followUps={copilot.followUps}
          onSend={vm.askCopilot}
          busy={copilot.busy === 'ask'}
          cost={copilot.costs.ask}
          disabled={vm.offline}
          disabledReason={t('visit.copilot.offline')}
        />
      )}

      {tab === 'differential' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Button
              size="sm"
              variant="outline"
              onClick={vm.requestDifferential}
              disabled={copilot.busy === 'differential'}
              data-testid="copilot-differential-run"
            >
              {copilot.differential ? t('visit.copilot.refresh') : t('visit.dx.differential')}
            </Button>
            <CostChip cost={copilot.costs.differential} before />
          </div>
          {!copilot.differential && (
            <p style={{ fontSize: 12.5, color: CT.mute, lineHeight: 1.9 }}>
              {t('visit.copilot.differentialEmpty')}
            </p>
          )}
          {copilot.differential && (
            <Card style={{ padding: 10 }} data-testid="copilot-differential-last">
              <p style={{
                margin: 0, fontSize: 12.5, lineHeight: 1.95, whiteSpace: 'pre-wrap', color: C.textPrimary,
              }}
              >
                {copilot.differential.narrative}
              </p>
              <Button
                size="sm"
                variant="ghost"
                style={{ marginTop: 8 }}
                onClick={() => vm.openSheet('differential')}
                data-testid="copilot-differential-open"
              >
                {t('visit.copilot.openCards')}
              </Button>
              <AiAnswerNote />
            </Card>
          )}
        </div>
      )}

      {tab === 'suggestions' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
          <Card style={{ padding: 10 }}>
            <strong style={{ fontSize: 12.5 }}>{t('visit.copilot.soapSuggestion')}</strong>
            {copilot.soapSuggestion ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => vm.openSheet('soapReview')}
                data-testid="copilot-soap-open"
              >
                {t('visit.copilot.reopen')}
              </Button>
            ) : (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: CT.mute }}>
                {t('visit.copilot.suggestionsEmpty')}
              </p>
            )}
          </Card>
          <Card style={{ padding: 10 }}>
            <strong style={{ fontSize: 12.5 }}>{t('visit.copilot.orderSuggestion')}</strong>
            {copilot.orderSuggestion ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => vm.openSheet('orderSuggest')}
                data-testid="copilot-orders-open"
              >
                {t('visit.copilot.reopen')}
              </Button>
            ) : (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: CT.mute }}>
                {t('visit.copilot.suggestionsEmpty')}
              </p>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
